import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import uuid from 'uuid';
import {
  IconButton,
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Container from '@grnhse/seedling/lib/birch/components/container';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import {
  Body,
  SecondaryBody,
  SectionTitle,
} from '@grnhse/seedling/lib/birch/typography';
import LoadingSpinner from '@grnhse/seedling/lib/birch/components/loading_spinner';
import { SecondaryTag } from '@grnhse/seedling/lib/birch/components/tags';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { MessageList } from 'shared/components/agent_chat/message_list';
import { MessageInput } from 'shared/components/agent_chat/message_input';
import { QuickReplyList } from 'shared/components/quick_reply_list';
import SliderComponent from 'talent_matching/components/calibration/slider';
import WorkTitleField from 'talent_matching/components/calibration/work_title_field';
import ChipWrapper from 'talent_matching/components/applied_filters/chip_wrapper';
import ClearableFilter from 'talent_matching/components/applied_filters/clearable_filter';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useCreateCalibrationSettings } from 'talent_matching/api';
import {
  trackedFilterEntries,
  trackUserAction,
  type TrackedFilter,
} from 'talent_matching/lib/tracker';
import { FORM_DEFAULTS } from 'talent_matching/filter_form_defaults';
import SidePanel, { SidePanelHandle } from 'shared/components/side_panel';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { useUserContext } from 'talent_matching/contexts/user_context';
import type { AdvancedFilterType } from 'talent_matching/types';
import {
  AppliedOnType,
  CalibrationSettings,
  ConfigurationAssistantFilters,
  ConfigurationAssistantProposal,
  Skill,
  TalentMatchingFilters,
} from 'talent_matching/types';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import type { ConfigurationAssistantMessage } from './use_configuration_assistant_chat';
import { useConfigurationAssistantChat } from './use_configuration_assistant_chat';

const t = withLocalePrefix('talent_matching.configuration_assistant');

// The setup panel is a review surface: Apply rebuilds filters from visible suggestions, not page state.
type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const SUGGESTED_SETUP_PANEL_WIDTH = 680;
const CONVERSATION_PANEL_WIDTH = 500;
const PANEL_WIDTH = SUGGESTED_SETUP_PANEL_WIDTH + CONVERSATION_PANEL_WIDTH;

type FilterKey =
  | 'location_name'
  | 'application_dates'
  | 'min_years_experience'
  | 'max_years_experience'
  | 'company_name_includes'
  | 'company_name_excludes'
  | 'must_have'
  | 'can_have'
  | 'does_not_have'
  | 'priority_candidates'
  | 'manual_review'
  | 'exclude_previously_rejected'
  | 'exclude_high_risk'
  | 'saved_for_later'
  | 'other_active_applications'
  | 'feedback_status';
type ResolvedFilterKey =
  | 'advanced_filters'
  | 'question_filters'
  | 'education_filters';
type FilterChipRemoveTarget =
  | FilterKey
  | { key: ResolvedFilterKey; identity: string };

const emptyFilters: ConfigurationAssistantFilters = {
  location_name: '',
  application_created_at: '',
  application_created_end: '',
  min_years_experience: null,
  max_years_experience: null,
  company_name_includes: [],
  company_name_excludes: [],
  must_have: [],
  can_have: [],
  does_not_have: [],
  priority_candidates: false,
  manual_review: false,
  exclude_previously_rejected: false,
  exclude_high_risk: false,
  saved_for_later: false,
  other_active_applications: '',
  feedback_status: '',
  advanced_filter_suggestions: [],
  advanced_filters: [],
  question_filters: [],
  education_filters: [],
};

const hasSuggestedCalibration = (calibration: CalibrationSettings | null) => {
  if (!calibration) {
    return false;
  }

  return (
    calibration.job_titles.length > 0 ||
    calibration.skills.length > 0 ||
    !!calibration.min_years_experience ||
    (calibration.industries || []).length > 0
  );
};

const hasSuggestedFilters = (filters: ConfigurationAssistantFilters) => {
  return (
    filters.location_name ||
    filters.application_created_at ||
    filters.application_created_end ||
    filters.min_years_experience ||
    filters.max_years_experience ||
    filters.company_name_includes.length > 0 ||
    filters.company_name_excludes.length > 0 ||
    filters.must_have.length > 0 ||
    filters.can_have.length > 0 ||
    filters.does_not_have.length > 0 ||
    filters.priority_candidates ||
    filters.manual_review ||
    filters.exclude_previously_rejected ||
    filters.exclude_high_risk ||
    filters.saved_for_later ||
    filters.other_active_applications ||
    filters.feedback_status ||
    filters.advanced_filters.length > 0 ||
    filters.question_filters.length > 0 ||
    filters.education_filters.length > 0
  );
};

const yearsLabel = (years: number) => t('filters.years', { count: years });

const applicationDateLabel = (filters: ConfigurationAssistantFilters) => {
  if (filters.application_created_at && filters.application_created_end) {
    return t('filters.application_between', {
      start: filters.application_created_at,
      end: filters.application_created_end,
    });
  }

  if (filters.application_created_at) {
    return t('filters.application_on_or_after', {
      start: filters.application_created_at,
    });
  }

  return t('filters.application_on_or_before', {
    end: filters.application_created_end,
  });
};

const hydrateAdvancedFilters = (filters: AdvancedFilterType[] | undefined) =>
  (filters || []).map((filter) => new AdvancedFilter(filter));

const isBlankAdvancedFilter = (filter: AdvancedFilter) =>
  !filter.type && !filter.operator && !filter.value;

const advancedFilterIdentity = (filter: AdvancedFilter) =>
  `${filter.queryKey()}:${JSON.stringify(filter.queryParamValue())}`;

const ADVANCED_FILTER_TRACKING_KEYS = [
  'advanced_filters',
  'question_filters',
  'education_filters',
] as const;

// Assistant-accepted filters in the proposal's snake_case vocabulary — not the
// camelCase form keys `filters_applied` reports after the apply resets the form.
const trackedSuggestedFilters = (
  filters: ConfigurationAssistantFilters
): TrackedFilter[] => {
  const simple = trackedFilterEntries(filters, [
    // advanced_filter_suggestions never reaches the form; the resolved
    // advanced_filters entries carry the same content.
    'advanced_filter_suggestions',
    ...ADVANCED_FILTER_TRACKING_KEYS,
  ]);

  const structured = ADVANCED_FILTER_TRACKING_KEYS.flatMap((name) => {
    const applied = hydrateAdvancedFilters(filters[name])
      .filter((filter) => filter.applied())
      .map((filter) => ({
        name: filter.queryKey(),
        values: filter.queryParamValue(),
      }));

    return applied.length > 0 ? [{ name, values: applied }] : [];
  });

  return [...simple, ...structured];
};

const buildFilterChips = (
  filters: ConfigurationAssistantFilters,
  userDateFormat: string,
  onRemove: (target: FilterChipRemoveTarget) => void
) => {
  const chips: React.ReactNode[] = [];

  if (filters.location_name) {
    chips.push(
      <SuggestedFilterChip
        key="location_name"
        label={t('filters.location', { location: filters.location_name })}
        onRemove={() => onRemove('location_name')}
      />
    );
  }

  if (filters.application_created_at || filters.application_created_end) {
    chips.push(
      <SuggestedFilterChip
        key="application_dates"
        label={applicationDateLabel(filters)}
        onRemove={() => onRemove('application_dates')}
      />
    );
  }

  if (filters.min_years_experience) {
    chips.push(
      <SuggestedFilterChip
        key="min_years_experience"
        label={t('filters.min_experience', {
          years: yearsLabel(filters.min_years_experience),
        })}
        onRemove={() => onRemove('min_years_experience')}
      />
    );
  }

  if (filters.max_years_experience) {
    chips.push(
      <SuggestedFilterChip
        key="max_years_experience"
        label={t('filters.max_experience', {
          years: yearsLabel(filters.max_years_experience),
        })}
        onRemove={() => onRemove('max_years_experience')}
      />
    );
  }

  if (filters.company_name_includes.length > 0) {
    chips.push(
      <SuggestedFilterChip
        key="company_name_includes"
        label={t('filters.company_name_includes', {
          companies: filters.company_name_includes.join(', '),
        })}
        onRemove={() => onRemove('company_name_includes')}
      />
    );
  }

  if (filters.company_name_excludes.length > 0) {
    chips.push(
      <SuggestedFilterChip
        key="company_name_excludes"
        label={t('filters.company_name_excludes', {
          companies: filters.company_name_excludes.join(', '),
        })}
        onRemove={() => onRemove('company_name_excludes')}
      />
    );
  }

  if (filters.must_have.length > 0) {
    chips.push(
      <SuggestedFilterChip
        key="must_have"
        label={t('filters.must_have', {
          keywords: filters.must_have.join(', '),
        })}
        onRemove={() => onRemove('must_have')}
      />
    );
  }

  if (filters.can_have.length > 0) {
    chips.push(
      <SuggestedFilterChip
        key="can_have"
        label={t('filters.can_have', {
          keywords: filters.can_have.join(', '),
        })}
        onRemove={() => onRemove('can_have')}
      />
    );
  }

  if (filters.does_not_have.length > 0) {
    chips.push(
      <SuggestedFilterChip
        key="does_not_have"
        label={t('filters.does_not_have', {
          keywords: filters.does_not_have.join(', '),
        })}
        onRemove={() => onRemove('does_not_have')}
      />
    );
  }

  if (filters.priority_candidates) {
    chips.push(
      <SuggestedFilterChip
        key="priority_candidates"
        label={t('filters.priority_candidates')}
        onRemove={() => onRemove('priority_candidates')}
      />
    );
  }

  if (filters.manual_review) {
    chips.push(
      <SuggestedFilterChip
        key="manual_review"
        label={t('filters.manual_review')}
        onRemove={() => onRemove('manual_review')}
      />
    );
  }

  if (filters.exclude_previously_rejected) {
    chips.push(
      <SuggestedFilterChip
        key="exclude_previously_rejected"
        label={t('filters.exclude_previously_rejected')}
        onRemove={() => onRemove('exclude_previously_rejected')}
      />
    );
  }

  if (filters.exclude_high_risk) {
    chips.push(
      <SuggestedFilterChip
        key="exclude_high_risk"
        label={t('filters.exclude_high_risk')}
        onRemove={() => onRemove('exclude_high_risk')}
      />
    );
  }

  if (filters.saved_for_later) {
    chips.push(
      <SuggestedFilterChip
        key="saved_for_later"
        label={t('filters.saved_for_later')}
        onRemove={() => onRemove('saved_for_later')}
      />
    );
  }

  if (filters.other_active_applications) {
    chips.push(
      <SuggestedFilterChip
        key="other_active_applications"
        label={t(
          filters.other_active_applications === 'include'
            ? 'filters.other_active_applications_include'
            : 'filters.other_active_applications_exclude'
        )}
        onRemove={() => onRemove('other_active_applications')}
      />
    );
  }

  if (filters.feedback_status) {
    chips.push(
      <SuggestedFilterChip
        key="feedback_status"
        label={t(`filters.feedback_status_${filters.feedback_status}`)}
        onRemove={() => onRemove('feedback_status')}
      />
    );
  }

  (
    ['advanced_filters', 'question_filters', 'education_filters'] as const
  ).forEach((key) => {
    hydrateAdvancedFilters(filters[key]).forEach((filter) => {
      if (!filter.applied()) {
        return;
      }

      const label = filter.appliedFilterText(userDateFormat);
      if (!label) {
        return;
      }

      const identity = advancedFilterIdentity(filter);
      chips.push(
        <SuggestedFilterChip
          key={`${key}:${identity}`}
          label={label}
          onRemove={() => onRemove({ key, identity })}
        />
      );
    });
  });

  return chips;
};

const mergeSuggestedAdvancedFilters = (
  currentFilters: AdvancedFilter[] | undefined,
  suggestedFilters: AdvancedFilter[]
) => {
  if (suggestedFilters.length === 0) {
    return currentFilters || [];
  }

  const current = (currentFilters || []).map(
    (filter) => new AdvancedFilter(filter)
  );
  const existingIdentities = new Set(
    current.filter((filter) => filter.applied()).map(advancedFilterIdentity)
  );
  const dedupedSuggestions = suggestedFilters.filter((filter) => {
    const identity = advancedFilterIdentity(filter);
    if (existingIdentities.has(identity)) {
      return false;
    }

    existingIdentities.add(identity);
    return true;
  });

  return [
    ...current.filter((filter) => !isBlankAdvancedFilter(filter)),
    ...dedupedSuggestions,
    ...current.filter(isBlankAdvancedFilter),
  ];
};

const defaultFilterFormValues = (
  currentValues: TalentMatchingFilters
): TalentMatchingFilters => ({
  ...FORM_DEFAULTS,
  page: 1,
  perPage: currentValues.perPage || FORM_DEFAULTS.perPage,
  tags: [...FORM_DEFAULTS.tags],
  degrees: [...FORM_DEFAULTS.degrees],
  disciplines: [...FORM_DEFAULTS.disciplines],
  schools: [...FORM_DEFAULTS.schools],
  jobPosts: [...FORM_DEFAULTS.jobPosts],
  excludedIds: [...FORM_DEFAULTS.excludedIds],
  bookmarkedByTeammates: [...FORM_DEFAULTS.bookmarkedByTeammates],
  companyNameFilters: [...FORM_DEFAULTS.companyNameFilters],
  educationFilters: [...FORM_DEFAULTS.educationFilters],
  advancedFilters: hydrateAdvancedFilters(FORM_DEFAULTS.advancedFilters),
  questionFilters: [...FORM_DEFAULTS.questionFilters],
  canHave: [...FORM_DEFAULTS.canHave],
  mustHave: [...FORM_DEFAULTS.mustHave],
  doesNotHave: [...FORM_DEFAULTS.doesNotHave],
});

const suggestedFilterFormValues = (
  filters: ConfigurationAssistantFilters,
  currentValues: TalentMatchingFilters
): TalentMatchingFilters => {
  const nextValues = defaultFilterFormValues(currentValues);

  if (filters.location_name) {
    nextValues.locationFilterType = 'name';
    nextValues.locationName = filters.location_name;
    nextValues.debouncedLocationName = filters.location_name;
  }

  if (filters.application_created_at || filters.application_created_end) {
    nextValues.appliedOnStart = filters.application_created_at || null;
    nextValues.appliedOnEnd = filters.application_created_end || null;

    if (filters.application_created_at && filters.application_created_end) {
      nextValues.appliedOnType = AppliedOnType.BETWEEN;
    } else if (filters.application_created_at) {
      nextValues.appliedOnType = AppliedOnType.ON_OR_AFTER;
    } else {
      nextValues.appliedOnType = AppliedOnType.ON_OR_BEFORE;
    }
  }

  if (filters.min_years_experience) {
    nextValues.minExperience = String(filters.min_years_experience);
  }

  if (filters.max_years_experience) {
    nextValues.maxExperience = String(filters.max_years_experience);
  }

  if (
    filters.company_name_includes.length > 0 ||
    filters.company_name_excludes.length > 0
  ) {
    const suggestedCompanyFilters = [
      ...filters.company_name_includes.map((value) => ({
        id: uuid.v4(),
        mode: 'include' as const,
        value,
      })),
      ...filters.company_name_excludes.map((value) => ({
        id: uuid.v4(),
        mode: 'exclude' as const,
        value,
      })),
    ];
    const companyFilterKeys = new Set<string>();

    nextValues.companyNameFilters = suggestedCompanyFilters.filter((filter) => {
      const key = `${filter.mode}:${filter.value.toLowerCase()}`;
      if (companyFilterKeys.has(key)) {
        return false;
      }

      companyFilterKeys.add(key);
      return true;
    });
  }

  if (filters.must_have.length > 0) {
    nextValues.mustHave = filters.must_have;
  }

  if (filters.can_have.length > 0) {
    nextValues.canHave = filters.can_have;
  }

  if (filters.does_not_have.length > 0) {
    nextValues.doesNotHave = filters.does_not_have;
  }

  if (filters.priority_candidates) {
    nextValues.priorityCandidates = true;
  }

  if (filters.manual_review) {
    nextValues.manualReview = true;
  }

  if (filters.exclude_previously_rejected) {
    nextValues.excludePreviouslyRejected = true;
  }

  if (filters.exclude_high_risk) {
    nextValues.excludeHighRisk = true;
  }

  if (filters.saved_for_later) {
    nextValues.savedForLater = 'saved';
  }

  if (filters.other_active_applications) {
    nextValues.otherActiveApplications =
      filters.other_active_applications === 'include' ? '1' : '0';
  }

  if (filters.feedback_status) {
    nextValues.feedbackStatus =
      filters.feedback_status === SCORECARD_STATUS.COMPLETED
        ? SCORECARD_STATUS.COMPLETED
        : SCORECARD_STATUS.INCOMPLETE;
  }

  nextValues.advancedFilters = mergeSuggestedAdvancedFilters(
    nextValues.advancedFilters,
    hydrateAdvancedFilters(filters.advanced_filters)
  );
  nextValues.questionFilters = mergeSuggestedAdvancedFilters(
    nextValues.questionFilters,
    hydrateAdvancedFilters(filters.question_filters)
  );
  nextValues.educationFilters = mergeSuggestedAdvancedFilters(
    nextValues.educationFilters,
    hydrateAdvancedFilters(filters.education_filters)
  );

  return nextValues;
};

const normalizedCalibration = (
  calibration: CalibrationSettings
): CalibrationSettings => ({
  ...calibration,
  skills: calibration.skills || [],
  job_titles: calibration.job_titles || [],
  use_boolean_search: !!calibration.use_boolean_search,
  prioritize_current_experience: !!calibration.prioritize_current_experience,
  industries: calibration.industries || [],
  extracted_skills: calibration.extracted_skills || [],
});

const ConfigurationAssistantPanel = ({ isOpen, onClose }: Props) => {
  const {
    paths,
    aiMatchEnabled,
    hasCalibration,
    initialAppliedFilterKeys,
    savedFilterSet,
    hasJobDescription,
    hasScorecardAttributes,
    canCreateCalibrationSetting,
    canEditAndDeleteTalentMatchingCalibration,
    appReviewSessionId,
  } = useTalentMatchingTabContext();
  const { organizationId, userId, hiringPlanId, userDateFormat } =
    useUserContext();
  const trackAttribution = {
    source: 'tm_app_review' as const,
    organizationId,
    userId,
    hiringPlanId,
    appReviewSessionId,
  };
  const filterForm = useFormContext<TalentMatchingFilters>();
  const panelRef = useRef<SidePanelHandle | null>(null);
  const popperRef = useRef<HTMLInputElement | null>(null);
  const hasInitialAppliedFilters =
    initialAppliedFilterKeys.length > 0 || savedFilterSet !== null;
  const showOpeningFlow = !hasCalibration && !hasInitialAppliedFilters;
  const canDraftFromRoleContext = hasJobDescription && hasScorecardAttributes;

  const {
    activeChatId,
    messages,
    isResponding,
    error,
    latestProposal,
    sendMessage,
  } = useConfigurationAssistantChat(paths.configurationAssistantChatsPath);

  const [inputValue, setInputValue] = useState('');
  const [proposal, setProposal] =
    useState<ConfigurationAssistantProposal | null>(null);
  const [suggestedCalibration, setSuggestedCalibration] =
    useState<CalibrationSettings | null>(null);
  const [showOpeningQuickReplies, setShowOpeningQuickReplies] = useState(true);
  const autoDraftStartedRef = useRef(false);
  // Mirrors the server's authorization: calibration proposals need the
  // create or edit calibration ability, not just AI match score visibility.
  const canManageCalibration = hasCalibration
    ? canEditAndDeleteTalentMatchingCalibration
    : canCreateCalibrationSetting;
  const shouldSuggestCalibration = aiMatchEnabled && canManageCalibration;

  const { mutate: createCalibrationSettings, isPending: isApplying } =
    useCreateCalibrationSettings(paths);

  const closePanel = useCallback(() => {
    if (panelRef.current?.isOpen()) {
      panelRef.current.close();
      return;
    }

    onClose();
  }, [onClose]);

  const openingMessage = useMemo<ConfigurationAssistantMessage | null>(() => {
    if (!showOpeningFlow) {
      return null;
    }

    let content = t('opening.ai_match_disabled');
    if (shouldSuggestCalibration && canDraftFromRoleContext) {
      content = t('opening.ai_match_enabled_with_context');
    } else if (shouldSuggestCalibration) {
      content = t('opening.ai_match_enabled');
    } else if (aiMatchEnabled) {
      content = t('opening.filters_only');
    }

    return {
      id: `assistant-opening-${
        shouldSuggestCalibration ? 'ai-match' : 'filters-only'
      }`,
      role: 'assistant',
      content,
    };
  }, [
    aiMatchEnabled,
    canDraftFromRoleContext,
    shouldSuggestCalibration,
    showOpeningFlow,
  ]);

  const visibleMessages = useMemo(
    () => messages.filter((message) => !message.hidden),
    [messages]
  );

  const displayedMessages = useMemo(() => {
    if (!openingMessage) {
      return visibleMessages;
    }

    return [openingMessage, ...visibleMessages];
  }, [openingMessage, visibleMessages]);

  const applyGeneratedProposal = useCallback(
    (nextProposal: ConfigurationAssistantProposal) => {
      setProposal(nextProposal);
      setSuggestedCalibration(
        normalizedCalibration(nextProposal.calibration_settings)
      );
    },
    []
  );

  // Apply each generated proposal exactly once; the panel stays mounted
  // across close/reopen, and re-applying would discard the user's edits.
  const lastAppliedProposalRef = useRef<ConfigurationAssistantProposal | null>(
    null
  );
  useEffect(() => {
    if (!latestProposal || latestProposal === lastAppliedProposalRef.current) {
      return;
    }

    lastAppliedProposalRef.current = latestProposal;
    applyGeneratedProposal(latestProposal);
  }, [applyGeneratedProposal, latestProposal]);

  const suggestedFilters = proposal?.filters || emptyFilters;
  const filterChips = useMemo(
    () =>
      buildFilterChips(suggestedFilters, userDateFormat, (target) => {
        setProposal((currentProposal) => {
          if (!currentProposal) {
            return currentProposal;
          }

          const nextFilters = { ...currentProposal.filters };

          if (typeof target !== 'string') {
            nextFilters[target.key] = hydrateAdvancedFilters(
              nextFilters[target.key]
            ).filter(
              (filter) => advancedFilterIdentity(filter) !== target.identity
            );

            return { ...currentProposal, filters: nextFilters };
          }

          const key = target;

          switch (key) {
            case 'application_dates':
              nextFilters.application_created_at = '';
              nextFilters.application_created_end = '';
              break;
            case 'company_name_includes':
            case 'company_name_excludes':
            case 'must_have':
            case 'can_have':
            case 'does_not_have':
              nextFilters[key] = [];
              break;
            case 'priority_candidates':
            case 'manual_review':
            case 'exclude_previously_rejected':
            case 'exclude_high_risk':
            case 'saved_for_later':
              nextFilters[key] = false;
              break;
            case 'location_name':
            case 'other_active_applications':
            case 'feedback_status':
              nextFilters[key] = '';
              break;
            case 'min_years_experience':
            case 'max_years_experience':
              nextFilters[key] = null;
              break;
          }

          return { ...currentProposal, filters: nextFilters };
        });
      }),
    [suggestedFilters, userDateFormat]
  );

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text || isResponding) {
      return;
    }

    setShowOpeningQuickReplies(false);
    sendMessage(text, { includeCalibration: shouldSuggestCalibration }).then(
      (acceptedChatId) => {
        if (acceptedChatId === null) {
          return;
        }

        trackUserAction({
          ...trackAttribution,
          category: 'interaction',
          action: 'assistant_message_sent',
          metadata: { origin: 'typed', chat_id: acceptedChatId },
        });
      }
    );
    setInputValue('');
  };

  const startSetupContext = () => {
    if (hasJobDescription && hasScorecardAttributes) {
      return t('opening.start_setup_context.both_available');
    }

    if (hasJobDescription) {
      return t('opening.start_setup_context.job_description_only');
    }

    if (hasScorecardAttributes) {
      return t('opening.start_setup_context.scorecard_only');
    }

    return t('opening.start_setup_context.neither_available');
  };

  const startSetupMessage = () => {
    const setupGoal = shouldSuggestCalibration
      ? t('opening.start_setup_goal.filters_and_calibration')
      : t('opening.start_setup_goal.filters_only');

    return t('opening.start_setup_prompt', {
      context: startSetupContext(),
      setup_goal: setupGoal,
    });
  };

  const handleStartSetup = () => {
    if (isResponding) {
      return;
    }

    setShowOpeningQuickReplies(false);
    sendMessage(startSetupMessage(), {
      includeCalibration: shouldSuggestCalibration,
      displayContent: t('opening.start_setup'),
    }).then((acceptedChatId) => {
      if (acceptedChatId === null) {
        return;
      }

      trackUserAction({
        ...trackAttribution,
        category: 'interaction',
        action: 'assistant_message_sent',
        metadata: { origin: 'start_setup', chat_id: acceptedChatId },
      });
    });
    setInputValue('');
  };

  useEffect(() => {
    if (
      !isOpen ||
      !showOpeningFlow ||
      !canDraftFromRoleContext ||
      autoDraftStartedRef.current ||
      messages.length > 0 ||
      activeChatId ||
      isResponding
    ) {
      return;
    }

    autoDraftStartedRef.current = true;
    setShowOpeningQuickReplies(false);
    sendMessage(
      shouldSuggestCalibration
        ? t('opening.auto_setup_prompt')
        : t('opening.auto_filters_only_prompt'),
      {
        includeCalibration: shouldSuggestCalibration,
        hidden: true,
      }
    );
  }, [
    activeChatId,
    canDraftFromRoleContext,
    isOpen,
    isResponding,
    messages.length,
    sendMessage,
    shouldSuggestCalibration,
    showOpeningFlow,
  ]);

  const handleHandleMyself = () => {
    closePanel();
  };

  const updateSuggestedCalibration = (
    updater: (calibration: CalibrationSettings) => CalibrationSettings
  ) => {
    setSuggestedCalibration((currentCalibration) =>
      currentCalibration ? updater(currentCalibration) : currentCalibration
    );
  };

  const applySuggestedFilters = (): TrackedFilter[] => {
    if (!proposal || !hasSuggestedFilters(proposal.filters)) {
      return [];
    }

    filterForm.reset(
      suggestedFilterFormValues(proposal.filters, filterForm.getValues())
    );
    return trackedSuggestedFilters(proposal.filters);
  };

  const applySuggestedCalibration = (
    afterSuccess: (calibrationApplied: boolean) => void
  ) => {
    if (!shouldSuggestCalibration) {
      afterSuccess(false);
      return;
    }

    if (
      !suggestedCalibration ||
      !hasSuggestedCalibration(suggestedCalibration)
    ) {
      afterSuccess(false);
      return;
    }

    createCalibrationSettings(
      {
        settings: {
          ...suggestedCalibration,
          dev_only_all_apps: false,
        },
        errorMessage: t('apply_error'),
      },
      {
        onSuccess: () => afterSuccess(true),
      }
    );
  };

  const handleApply = () => {
    applySuggestedCalibration((calibrationApplied) => {
      const appliedFilters = applySuggestedFilters();
      // A proposal can be all-conversation (no filters or calibration); Apply
      // is then a no-op and must not count as an applied setup.
      if (calibrationApplied || appliedFilters.length > 0) {
        trackUserAction({
          ...trackAttribution,
          category: 'action',
          action: 'assistant_setup_applied',
          filters: appliedFilters.length > 0 ? appliedFilters : undefined,
          metadata: {
            applied_calibration: calibrationApplied,
            applied_filters: appliedFilters.length > 0,
            chat_id: activeChatId,
          },
        });
      }
      closePanel();
    });
  };

  const suggestedSetupLoadingDescription = shouldSuggestCalibration
    ? t(proposal ? 'suggested_setup_loading' : 'suggested_setup_pending')
    : t(proposal ? 'suggested_filters_loading' : 'suggested_filters_pending');
  let suggestedSetupDescription = shouldSuggestCalibration
    ? t('suggested_setup_description')
    : t('suggested_filters_description');
  if (isResponding) {
    suggestedSetupDescription = suggestedSetupLoadingDescription;
  }
  const isSuggestedSetupUpdating = isResponding;

  return (
    <SidePanel
      ref={panelRef}
      id="configuration_assistant_panel"
      isVisible={isOpen}
      onClose={onClose}
      width={PANEL_WIDTH}
      disableClickOutside={isApplying}
    >
      <PanelShell>
        <SuggestedSetupPane>
          <PanelHeader>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SectionTitle large>{t('suggested_setup_title')}</SectionTitle>
                {isSuggestedSetupUpdating && (
                  <InlineLoadingStatus
                    role="status"
                    aria-label={suggestedSetupDescription}
                  >
                    <LoadingSpinner size="small" />
                  </InlineLoadingStatus>
                )}
              </Stack>
              <SecondaryBody>{suggestedSetupDescription}</SecondaryBody>
            </Stack>
          </PanelHeader>

          <PanelBody>
            <PanelContent spacing={2}>
              <PreviewGrid>
                {shouldSuggestCalibration &&
                  (!suggestedCalibration && isSuggestedSetupUpdating ? (
                    <PreviewPanel>
                      <Stack spacing={1}>
                        <Body medium>{t('calibration_title')}</Body>
                        <SuggestedSetupLoadingState
                          label={suggestedSetupLoadingDescription}
                        />
                      </Stack>
                    </PreviewPanel>
                  ) : (
                    <SuggestedCalibrationPanel
                      calibration={suggestedCalibration}
                      updateCalibration={updateSuggestedCalibration}
                      popperRef={popperRef}
                    />
                  ))}

                <PreviewPanel>
                  <Stack spacing={1}>
                    <Body medium>{t('filters_title')}</Body>
                    {filterChips.length > 0 ? (
                      <BaseAppliedFiltersRow
                        chips={filterChips}
                        collapse={false}
                      />
                    ) : isSuggestedSetupUpdating ? (
                      <SuggestedSetupLoadingState
                        label={suggestedSetupLoadingDescription}
                      />
                    ) : (
                      <SecondaryBody>{t('no_filters')}</SecondaryBody>
                    )}
                  </Stack>
                </PreviewPanel>
              </PreviewGrid>
            </PanelContent>
          </PanelBody>

          <PanelFooter>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AiFooterText>{t('ai_footer')}</AiFooterText>
              <SecondaryButton onClick={closePanel}>
                {t('cancel')}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleApply}
                disabled={!proposal || isSuggestedSetupUpdating || isApplying}
              >
                {isApplying ? t('apply_loading') : t('apply')}
              </PrimaryButton>
            </Stack>
          </PanelFooter>
        </SuggestedSetupPane>

        <ConversationPane>
          <PanelHeader>
            <PanelTitleRow>
              <AiMagicIcon size="md" color={FullPalette.VIOLET_600} />
              <SectionTitle large>{t('title')}</SectionTitle>
              <Tooltip label={t('beta_tooltip')} ariaLabel={t('beta_tooltip')}>
                <SecondaryTag text={t('beta_tag')} />
              </Tooltip>
            </PanelTitleRow>
            <IconButton
              icon={CloseIcon}
              size="md"
              onClick={closePanel}
              label={t('close')}
              flat
            />
          </PanelHeader>

          <PanelBody>
            <ConversationContent spacing={2}>
              <ChatSection>
                <ChatFrame>
                  {displayedMessages.length === 0 && !isResponding ? (
                    <EmptyChatState>
                      <Body>
                        {shouldSuggestCalibration
                          ? t('empty_chat')
                          : t('empty_chat_filters_only')}
                      </Body>
                    </EmptyChatState>
                  ) : (
                    <MessageList
                      messages={displayedMessages}
                      streamingContent=""
                      isStreaming={isResponding}
                      labels={{
                        thinking: t('thinking'),
                      }}
                    >
                      {showOpeningFlow &&
                        showOpeningQuickReplies &&
                        !canDraftFromRoleContext &&
                        messages.length === 0 &&
                        !isResponding && (
                          <QuickReplyList
                            replies={[
                              {
                                id: 'start_setup',
                                label: t('opening.start_setup'),
                                onClick: handleStartSetup,
                              },
                              {
                                id: 'handle_myself',
                                label: t('opening.handle_myself'),
                                onClick: handleHandleMyself,
                              },
                            ]}
                          />
                        )}
                    </MessageList>
                  )}
                </ChatFrame>
                {error && <ErrorText>{error}</ErrorText>}
              </ChatSection>
            </ConversationContent>
          </PanelBody>

          <ConversationFooter>
            <MessageInput
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSendMessage}
              disabled={isResponding}
              placeholder={t('message_placeholder')}
              sendLabel={t('message_send_label')}
            />
          </ConversationFooter>
        </ConversationPane>
      </PanelShell>
    </SidePanel>
  );
};

const SuggestedFilterChip = ({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) => (
  <ChipWrapper>
    <ClearableFilter text={label} onRemove={onRemove} />
  </ChipWrapper>
);

const SuggestedSetupLoadingState = ({ label }: { label: string }) => (
  <LoadingRow>
    <SecondaryBody>{label}</SecondaryBody>
  </LoadingRow>
);

const SuggestedCalibrationPanel = ({
  calibration,
  updateCalibration,
  popperRef,
}: {
  calibration: CalibrationSettings | null;
  updateCalibration: (
    updater: (currentCalibration: CalibrationSettings) => CalibrationSettings
  ) => void;
  popperRef: React.RefObject<HTMLInputElement>;
}) => {
  if (!calibration) {
    return (
      <PreviewPanel>
        <Stack spacing={1}>
          <Body medium>{t('calibration_title')}</Body>
          <SecondaryBody>{t('no_calibration')}</SecondaryBody>
        </Stack>
      </PreviewPanel>
    );
  }

  const removeSkill = (skillName: string) => {
    updateCalibration((currentCalibration) => ({
      ...currentCalibration,
      skills: currentCalibration.skills.filter(
        (skill) => skill.name !== skillName
      ),
    }));
  };

  const setSkillValue = (skill: Skill, value: number) => {
    updateCalibration((currentCalibration) => ({
      ...currentCalibration,
      skills: currentCalibration.skills.map((currentSkill) =>
        currentSkill.name === skill.name
          ? { ...currentSkill, importance: value }
          : currentSkill
      ),
    }));
  };

  const addWorkTitles = (jobTitles: string[]) => {
    updateCalibration((currentCalibration) => ({
      ...currentCalibration,
      job_titles: [...new Set(jobTitles)],
    }));
  };

  const removeWorkTitle = (jobTitle: string) => {
    updateCalibration((currentCalibration) => ({
      ...currentCalibration,
      job_titles: currentCalibration.job_titles.filter(
        (currentTitle) => currentTitle !== jobTitle
      ),
    }));
  };

  return (
    <PreviewPanel>
      <Stack spacing={2}>
        <Body medium>{t('calibration_title')}</Body>
        <Stack spacing={1}>
          {calibration.skills.length > 0 ? (
            calibration.skills.map((skill) => (
              <SliderComponent
                key={skill.name}
                value={skill.importance}
                name={skill.name}
                onRemove={removeSkill}
                onChange={(value) => setSkillValue(skill, value)}
              />
            ))
          ) : (
            <SecondaryBody>{t('no_skills')}</SecondaryBody>
          )}
        </Stack>
        <Stack spacing={1.5}>
          <TextInput
            label={t('min_years_label')}
            type="number"
            min={0}
            max={50}
            value={String(calibration.min_years_experience ?? '')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const nextValue = parseInt(event.target.value, 10);
              updateCalibration((currentCalibration) => ({
                ...currentCalibration,
                min_years_experience: Number.isNaN(nextValue) ? '' : nextValue,
              }));
            }}
          />
          <WorkTitleField
            onAdd={addWorkTitles}
            onRemove={removeWorkTitle}
            values={calibration.job_titles}
            inputRef={popperRef}
            booleanIndicator="or"
          />
          <Checkbox
            label={t('include_similar_roles')}
            value="use_boolean_search"
            checked={calibration.use_boolean_search}
            onChange={(checked) => {
              updateCalibration((currentCalibration) => ({
                ...currentCalibration,
                use_boolean_search: checked,
              }));
            }}
          />
          <Checkbox
            label={t('prioritize_current_experience')}
            value="prioritize_current_experience"
            checked={!!calibration.prioritize_current_experience}
            onChange={(checked) => {
              updateCalibration((currentCalibration) => ({
                ...currentCalibration,
                prioritize_current_experience: checked,
              }));
            }}
          />
        </Stack>
      </Stack>
    </PreviewPanel>
  );
};

const PanelShell = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${CONVERSATION_PANEL_WIDTH}px;
  min-height: 0;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SuggestedSetupPane = styled.div`
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${FullPalette.GREY_600};
`;

const ConversationPane = styled.div`
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${FullPalette.WHITE};
`;

const PanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Tokens.spacing['2']};
  padding: ${Tokens.spacing['2']};
  border-bottom: 1px solid ${FullPalette.GREY_600};
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing['1']};
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
`;

const PanelContent = styled(Stack)`
  padding: ${Tokens.spacing['3']};
`;

const PanelFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: ${Tokens.spacing['1']};
  padding: ${Tokens.spacing['2']} ${Tokens.spacing['3']};
  border-top: 1px solid ${FullPalette.GREY_600};
  background: ${FullPalette.WHITE};
`;

const ConversationFooter = styled.div`
  flex-shrink: 0;
  background: ${FullPalette.WHITE};
`;

const AiFooterText = styled(SecondaryBody)`
  margin-right: ${Tokens.spacing['6']};
`;

const ConversationContent = styled(Stack)`
  height: 100%;
  min-height: 0;
  padding: ${Tokens.spacing['3']};
`;

const ChatSection = styled(Stack)`
  flex: 1;
  min-height: 0;
`;

const ChatFrame = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EmptyChatState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Tokens.spacing['3']};
  text-align: center;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${Tokens.spacing['2']};
  align-items: start;
`;

const PreviewPanel = styled(Container)`
  background-color: ${FullPalette.WHITE};
  border-color: ${FullPalette.GREY_600};
  box-shadow: none;
`;

const InlineLoadingStatus = styled.div`
  display: flex;
  align-items: center;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  min-height: ${Tokens.spacing['4']};
`;

const ErrorText = styled(SecondaryBody)`
  color: ${Tokens.colors.error};
`;

export default ConfigurationAssistantPanel;
